import { useEffect, useRef, useState } from "react";
import { embedSvgFonts } from "@/lib/svgFontEmbed";

interface SvgPreviewProps {
  /** Raw SVG source from the editor. */
  code: string;
}

/** Debounce for object-URL recreation so per-keystroke edits don't thrash
 *  the blob/URL churn. */
const REBUILD_DELAY_MS = 300;

/**
 * SVG-session preview. Renders the editor's SVG source through an <img>
 * (img-src can't execute embedded scripts, so this sandboxes the graphic)
 * on a subtle checkerboard so transparency reads clearly. The object URL is
 * rebuilt on a debounce and the stale one revoked to avoid leaking blobs.
 */
export function SvgPreview({ code }: SvgPreviewProps) {
  const isSvg = code.includes("<svg");
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  // Monotonic token: font-embedding is async, so a later edit can finish its
  // embed BEFORE an earlier one. Every effect run claims the next seq; a
  // resolved embed only commits its blob URL if it still owns the latest seq,
  // so stale results are dropped instead of overwriting a newer preview.
  const seqRef = useRef(0);

  useEffect(() => {
    const seq = ++seqRef.current;
    if (!isSvg) {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setUrl(null);
      return;
    }
    const handle = setTimeout(() => {
      void embedSvgFonts(code).then((embedded) => {
        if (seq !== seqRef.current) return; // superseded by a newer edit
        const next = URL.createObjectURL(
          new Blob([embedded], { type: "image/svg+xml" }),
        );
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = next;
        setUrl(next);
      });
    }, REBUILD_DELAY_MS);
    return () => clearTimeout(handle);
  }, [code, isSvg]);

  // Revoke the last live URL when the preview unmounts.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return (
    <div className="relative m-auto flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-surface-2 text-ink-3">
      {/* Checkerboard — currentColor (text-ink-3) at low opacity so it reads
          the same in light and dark without hard-coded hex. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "conic-gradient(currentColor 90deg, transparent 90deg 180deg, currentColor 180deg 270deg, transparent 270deg)",
          backgroundSize: "24px 24px",
        }}
      />
      {isSvg && url ? (
        <img
          src={url}
          alt="SVG preview"
          className="relative max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="relative text-sm text-ink-3">Not a valid SVG yet</span>
      )}
    </div>
  );
}
